import classNames from 'classnames/bind';
import { gql } from '@apollo/client';
import Link from 'next/link';
import styles from './HeaderNavigationMenu.module.scss';
import { flatListToHierarchical } from '@faustwp/core';
import { Button, ChevronDown } from '@/components';
import React, { useEffect, useRef, useState } from 'react';
import checkForExternalLink from '@/helpers/checkForExternalLink';
import { useRouter } from 'next/compat/router';

let cx = classNames.bind(styles);

export function HeaderNavigationMenu({ menuItems, className, handleNavClose}) {
	if (!menuItems) {
		return null;
	}
	const [openSubmenu, setOpenSubmenu] = useState(null);
	let subMenuRef = useRef(null);
	const currentPathname = useRouter().asPath.split(/[?#]/)[0];

	function handleNavigaitonMenuDisplay(e) {
		if (subMenuRef.current === null) {
			// no parent caregory has been selected yet
			setOpenSubmenu(e.target.parentElement.dataset.id);
			subMenuRef.current = e.target.parentElement;
		} else if (subMenuRef.current.querySelector('button') !== e.target && e.target.classList.contains('ns_parent-category')) {
			// different parent category button selected
			setOpenSubmenu(e.target.parentElement.dataset.id);
			subMenuRef.current = e.target.parentElement;
		} else if (!subMenuRef.current.contains(e.target) || subMenuRef.current.querySelector('button') === e.target) {
			// clicked outside the category or a click on the current category button
			setOpenSubmenu(null);
			subMenuRef.current = null;
			document.body.removeEventListener('click', handleNavigaitonMenuDisplay);
		}
	}

	function addNavigaitonClickListener() {
		if (subMenuRef.current === null) {
			document.body.addEventListener('click', handleNavigaitonMenuDisplay);
		}
	}

	function InfoChip({ text }) {
		return <span className={cx(['ns_info_chip', 'info-chip'])}>{text}</span>;
	}

	// Display when:
	// 1. no children and no link path
	function NoLinkText({ menuItem }) {
		const { label, navItemSettings } = menuItem;
		return (
			<span className={cx(['ns_menu-text', 'menu__text'])}>
				<div className={cx('label-container')}>
					{label ?? ''}
					{navItemSettings?.infoChip && <InfoChip text={navItemSettings.infoChip} />}
				</div>
			</span>
		);
	}

	// Display when:
	// 1. has children and no link path, and,
	// 2. is mobile nav and is a child, or, is desktop nav and is not a child
	function ParentCategoryButton({ menuItem, parentMenuId }) {
		const { id, label, children, navItemSettings } = menuItem;
		return (
			<button id={`parent-category-${id}`} type="button" className={cx(['ns_parent-category', 'parentCategory'])} onClick={addNavigaitonClickListener} aria-expanded={openSubmenu === id ? true : false} aria-controls={`sub-menu-${id}`}>
				<div className={cx('label-container')}>
					{label ?? ''}
					{navItemSettings?.infoChip && <InfoChip text={navItemSettings.infoChip} />}
				</div>
				{parentMenuId && children.length ? <ChevronDown className={cx('mobile-nav-chevron')} /> : ''}
			</button>
		);
	}

	// Display when:
	// 1. has children and no link path, and,
	// 2. is mobile nav is not a child, or, is desktop nav and is a child
	function ParentCategoryHeading({ menuItem }) {
		const { id, label, navItemSettings } = menuItem;
		return (
			<span id={`parent-category-${id}`} className={cx(['ns_parent-category', 'parentCategory'])}>
				<div className={cx('label-container')}>
					{label ?? ''}
					{navItemSettings?.infoChip && <InfoChip text={navItemSettings.infoChip} />}
				</div>
			</span>
		);
	}

	function PickElement({ isMobile, parentMenuId, menuItem }) {
		if (!menuItem.children.length) {
			return <NoLinkText menuItem={menuItem} />;
		} else {
			if ((isMobile && parentMenuId) || (!isMobile && !parentMenuId)) {
				return <ParentCategoryButton menuItem={menuItem} parentMenuId={parentMenuId} />;
			} else {
				return <ParentCategoryHeading menuItem={menuItem} />;
			}
		}
	}

	// Based on https://www.wpgraphql.com/docs/menus/#hierarchical-data
	const hierarchicalMenuItems = flatListToHierarchical(menuItems);
	function renderMenu(items, parentMenuId = null, isMobile) {
		const rowCount = parentMenuId && items.length >= 6 ? Math.ceil(items.length / 2) : null;
		const style = rowCount
			? {
					gridTemplateRows: `repeat(${rowCount}, 1fr)`,
					gridTemplateColumns: `repeat(2, 1fr)`,
			  }
			: null;
		return (
			<ul id={parentMenuId && `sub-menu-${parentMenuId}`} className={cx(['ns_menu-ul', 'menu__ul', !parentMenuId && isMobile && 'menu-mobile', !parentMenuId && !isMobile && 'menu-desk'])} style={style} hidden={!isMobile || (parentMenuId && openSubmenu === parentMenuId) ? false : true} aria-labelledby={parentMenuId && `parent-category-${parentMenuId}`}>
				{items.map((item) => {
					const { id, path, label, children, cssClasses, target, connectedNode, navItemSettings } = item;
					const elem = connectedNode ? Link : 'a';
					const isCurrentPage = `${currentPathname}` === path;

					// @TODO - Remove guard clause after ghost menu items are no longer appended to array.
					if (!item.hasOwnProperty('__typename')) {
						return null;
					}
					return (
						<li key={id} className={cx(['ns_menu-li', 'menu__li', openSubmenu === id && 'submenu-is-open', cssClasses])} data-id={id}>
							{path ? (
								React.createElement(
									elem,
									{
										href: path,
										className: cx(['menu__a', children.length && 'parentCategory', isCurrentPage && 'current-page']),
										onClick: handleNavClose,
										target: target,
									},
									[label ?? '', navItemSettings?.infoChip && <InfoChip text={navItemSettings.infoChip} />]
								)
							) : (
								<PickElement isMobile={isMobile} parentMenuId={parentMenuId} menuItem={item} />
							)}
							{children.length ? renderMenu(children, id, isMobile) : null}
						</li>
					);
				})}
			</ul>
		);
	}

	return (
		<nav id="primary-navigation" className={cx(['nav', className])} role="navigation" aria-label={`${menuItems[0]?.menu?.node?.name} menu`}>
			<div className={cx('spacer')} />
			{renderMenu(hierarchicalMenuItems, null, true)}
			{renderMenu(hierarchicalMenuItems, null, false)}
		</nav>
	);
}

HeaderNavigationMenu.fragments = {
	entry: gql`
		fragment HeaderNavigationMenuItemFragment on MenuItem {
			id
			path
			label
			parentId
			cssClasses
			menu {
				node {
					name
				}
			}
			navItemSettings {
				infoChip
			}
			connectedNode {
				node {
					id
				}
			}
			target
		}
	`,
};
